import Controller from "controllers/application_controller"
import { PopoverPositioning } from "../lib/popover_positioning"
import { parse } from "../lib/date_utils"

const labelFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })

/**
 * Date picker: shadcn's Popover + outline Button + Calendar recipe (see
 * date_picker.css). The Popover API opens and light-dismisses the calendar,
 * and ui-popover mounts it from a template on each open. This controller
 * positions it, shows the calendar on open, and commits a
 * `ui-calendar:select` to the hidden input. The trigger label follows the
 * input's `input` event, so a script that sets the value dispatches `input`,
 * as it would for a native field.
 *
 * Escape is claimed here, as in ui-select and ui-dropdown-menu: cancelling it
 * stops Ui::Modal (or another document-level handler) closing around us.
 * Tabbing out of the picker closes it; clicks on the popover's own padding do
 * not, because their focusout has no related target.
 */
export default class extends Controller {
  static identifier = "ui-date-picker"
  static targets = ["trigger", "content", "label", "input"]
  static values = { placeholder: String }

  connect() {
    this.positioning = new PopoverPositioning({
      reference: this.triggerTarget,
      floating: this.contentTarget,
      namePrefix: "ui-date-picker"
    })

    this.addEventListener(this.contentTarget, "toggle", event => this.toggled(event))
    this.addEventListener(this.contentTarget, "keydown", event => this.keydown(event))
    this.addEventListener(this.contentTarget, "ui-calendar:select", event => this.selected(event))
    this.addEventListener(this.element, "focusout", event => this.focusLeft(event))
    this.addEventListener(this.inputTarget, "input", () => this.showValue())
  }

  disconnect() {
    this.positioning?.stop()
    super.disconnect()
  }

  toggled(event) {
    const open = event.newState === "open"
    this.triggerTarget.setAttribute("aria-expanded", String(open))

    if (open) {
      this.positioning.start()
      this.showCalendar()
    } else {
      this.positioning.stop()
    }
  }

  // A freshly mounted calendar carries the date the page rendered with, so it
  // takes the committed date before showing.
  showCalendar() {
    const calendar = this.childController("ui-calendar")
    calendar.selectedValue = this.inputTarget.value
    calendar.show()
  }

  selected({ detail: { date } }) {
    this.inputTarget.value = date
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  showValue() {
    const date = this.inputTarget.value

    if (date) {
      this.labelTarget.textContent = labelFormat.format(parse(date))
    } else {
      this.labelTarget.textContent = this.placeholderValue
    }

    this.triggerTarget.toggleAttribute("data-empty", !date)
  }

  keydown(event) {
    if (event.key !== "Escape") return

    event.preventDefault()
    event.stopPropagation()
    this.close()
  }

  focusLeft(event) {
    if (!event.relatedTarget) return
    if (this.element.contains(event.relatedTarget)) return

    this.hide()
  }

  // Focus returns to the trigger explicitly: a cancelled Escape also cancels the
  // popover's own focus restore.
  close() {
    this.hide()
    this.triggerTarget.focus()
  }

  hide() {
    // A close landing during the exit fade would throw InvalidStateError
    if (this.contentTarget.matches(":popover-open")) this.contentTarget.hidePopover()
  }
}
